import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import MultilingualButton from '../components/MultilingualButton';
import FAQ from '../components/FAQ';
import HotTopics from '../components/HotTopics/HotTopics';
import TphileTokenInfo from '../components/TphileTokenInfo';

import { 
  TrendingUp, 
  Zap, 
  BarChart3, 
  Eye,
  Star,
  Users,
  Target,
  Globe,
  Mail,
  ArrowRight,
  Sparkles,
  Lightbulb,
  Shield,
  Heart,
  MessageCircle,
  CheckCircle,
  AlertTriangle,
  BookOpen,
  Award
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useStructuredData } from '../hooks/useStructuredData';
import './Home.css';

// Optimized Performance Monitor Component
const PerformanceMonitor = memo(() => {
  useEffect(() => {
    let frameCount = 0;
    let lastTime = performance.now();
    let animationId;

    const measureFPS = () => {
      frameCount++;
      const currentTime = performance.now();
      
      if (currentTime >= lastTime + 1000) {
        const fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        
        // Only log if FPS is concerning
        if (fps < 30) {
          console.warn(`🔴 Low FPS detected: ${fps}fps - Consider optimizations`);
        }
        
        frameCount = 0;
        lastTime = currentTime;
      }
      
      animationId = requestAnimationFrame(measureFPS);
    };

    // Only monitor in development
    if (process.env.NODE_ENV === 'development') {
      animationId = requestAnimationFrame(measureFPS);
    }

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  return null;
});

function Home() {
  const navigate = useNavigate();
  const observerRef = useRef();
  const backgroundElementsCreated = useRef(false);
  const { isLoggedIn } = useAuth();

  // SEO Configuration
  const seoConfig = {
    title: "TrendPhile - Trend Prediction Platform | Share Your Vision of Tomorrow",
    description: "Join TrendPhile, the leading AI-powered prediction platform where users share and analyze future trends. Discover comprehensive insights on fashion, technology, market forecasting, and social predictions for 2025 and beyond. Connect with trend experts and make informed decisions based on collective intelligence and data-driven analysis.",
    keywords: "trend predictions, prediction platform, future trends, market forecasting, 2025 predictions, trend analysis, fashion trend predictions, technology predictions, social trends",
    canonical: "https://trendphile.com/",
    ogImage: "https://trendphile.com/tp1logo-whatsapp.jpg"
  };

  useSEO(seoConfig);

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "TrendPhile - Home",
    "description": "Discover and analyze the latest trends across various industries",
    "url": "https://trendphile.com/",
    "mainEntity": {
      "@type": "ItemList",
      "name": "Latest Trends",
      "description": "Current trending topics and analysis"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://trendphile.com/"
        }
      ]
    }
  };

  useStructuredData(structuredData);
  
  // State for real stats
  const [stats, setStats] = useState({
    totalUsers: 2898,
    predictionsToday: 567,
    predictorsToday: 87,
    dailyPredictions: 567,
    activePredictors: 150,
    totalPredictions: 25
  });

  const API_BASE = process.env.REACT_APP_API_URL || '/api';

  // Optimized stats fetching with error boundary
  const fetchStats = useCallback(async () => {
    try {
      console.log('🔄 Fetching homepage stats...');
      const response = await fetch(`${API_BASE}/stats/homepage`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Received stats data:', data);
        
        setStats({
          totalUsers: data.totalUsers || 10,
          predictionsToday: data.predictionsToday || 0,
          predictorsToday: data.predictorsToday || 0,
          dailyPredictions: data.dailyPredictions || 0,
          activePredictors: data.activePredictors || 0,
          totalPredictions: data.dataPoints || 25
        });
        
        console.log('✅ Stats updated successfully');
      } else {
        console.error('❌ Failed to fetch stats, status:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching homepage stats:', error);
      // Keep fallback values on error
    }
  }, [API_BASE]);

  // Helper function to format numbers
  const formatNumber = useCallback((num) => {
    if (num === undefined || num === null || isNaN(num)) {
      return '0';
    }
    
    const number = typeof num === 'string' ? parseInt(num) || 0 : num;
    return number.toLocaleString();
  }, []);
  
  // Optimized action handlers
  const handlePrimaryAction = useCallback(() => {
    if (isLoggedIn) {
      navigate('/submit');
    } else {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  const handleSecondaryAction = useCallback(() => {
    navigate('/analytics');
  }, [navigate]);

  const handlePredictionSubmit = useCallback(() => {
    if (isLoggedIn) {
      navigate('/submit');
    } else {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  // Optimized background elements creation
  useEffect(() => {
    const createBackgroundElements = () => {
      if (backgroundElementsCreated.current) return;

      // Remove existing elements safely
      const existingElements = document.querySelectorAll('[data-home-bg="true"]');
      existingElements.forEach(el => {
        if (el?.parentNode) {
          el.parentNode.removeChild(el);
        }
      });

      try {
        // Create elements using DocumentFragment for better performance
        const fragment = document.createDocumentFragment();
        
        const gridElement = document.createElement('div');
        gridElement.className = 'background-grid';
        gridElement.setAttribute('data-home-bg', 'true');
        fragment.appendChild(gridElement);

        const glow1 = document.createElement('div');
        glow1.className = 'ambient-glow';
        glow1.setAttribute('data-home-bg', 'true');
        fragment.appendChild(glow1);

        const glow2 = document.createElement('div');
        glow2.className = 'ambient-glow-2';
        glow2.setAttribute('data-home-bg', 'true');
        fragment.appendChild(glow2);

        document.body.appendChild(fragment);
        backgroundElementsCreated.current = true;
      } catch (error) {
        console.error('Error creating background elements:', error);
      }
    };

    createBackgroundElements();

    return () => {
      backgroundElementsCreated.current = false;
      
      // Cleanup with requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        const elements = document.querySelectorAll('[data-home-bg="true"]');
        elements.forEach(el => {
          if (el?.parentNode?.contains(el)) {
            el.parentNode.removeChild(el);
          }
        });
      });
    };
  }, []);

  // Combined intersection observer setup
  useEffect(() => {
    fetchStats();

    // Setup intersection observer for animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      },
      { 
        threshold: 0.1, 
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Setup animation observer for two-column sections
    const colObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          entry.target.classList.toggle('in-view', entry.isIntersecting);
        });
      }, 
      { threshold: 0.25 }
    );

    // Batch DOM queries for better performance
    const animateElements = document.querySelectorAll('.feature-card, .stat-card, .prediction-prompt, .about-card, .guideline-card, .tphile-token-container, .earning-method-card, .highlight-item, .hot-topics-header, .topic-box');
    const columnElements = document.querySelectorAll('.features-two-column, .about-two-column, .guidelines-two-column');
    
    // Setup animations with batch processing
    requestAnimationFrame(() => {
      animateElements.forEach((el) => {
        el.style.opacity = '0';
        el.style.transform = 'translateY(30px)';
        el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        observerRef.current.observe(el);
      });

      columnElements.forEach(el => colObserver.observe(el));
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      colObserver.disconnect();
    };
  }, [fetchStats]);

  return (
    <div className="home-container">
      <PerformanceMonitor />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <div className="hero-badge">
                <TrendingUp className="hero-badge-icon" />
                <span>Trendphile</span>
              </div>
              
              <h1 className="hero-title">
                <span className="hero-title-line hero-title-main">
                  Share Your Vision of
                </span>
                <span className="hero-title-line hero-title-accent">
                  Tomorrow's World
                </span>
              </h1>
              
              <p className="hero-subtitle">
                Join a thriving platform designed for visionaries and forward-thinkers who dare to predict what's coming next. Your insights matter, your predictions shape conversations, and together we're creating a fascinating glimpse into the future across technology, culture, society, and beyond.Who dare to predict what is coming next or express expectations.
              </p>
              
              <div className="hero-cta-group">
                <MultilingualButton 
                  className="cta-primary" 
                  onClick={handlePrimaryAction} 
                  isLoggedIn={isLoggedIn} 
                  icon={<Zap size={20} />}
                >
                  {isLoggedIn ? 'Share Your Prediction or Expectation' : 'Join & Predict'}
                </MultilingualButton>
                <button className="cta-secondary" onClick={handleSecondaryAction}>
                  <BarChart3 />
                  Explore Analytics
                </button>
              </div>
            </div>

            <div className="hero-visual">
              <div className="hero-dashboard">
                <div className="dashboard-header">
                  <div className="dashboard-title">Trendphile Prediction Hub</div>
                </div>
                <div className="dashboard-metrics">
                  <div className="metric-card">
                    <div className="metric-value">{stats.totalUsers.toLocaleString()}</div>
                    <div className="metric-label">Predictors</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{formatNumber(stats.totalPredictions)}</div>
                    <div className="metric-label">Predictions</div>
                  </div>
                </div>
                <div className="dashboard-chart">
                  <div className="chart-line"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hot Topics Section */}
      <section className="hot-topics-section">
        <div className="container">
          <HotTopics />
        </div>
      </section>

      {/* TPHILE Token Section */}
      <section className="tphile-token-section">
        <div className="container">
          <TphileTokenInfo isLoggedIn={isLoggedIn} />
        </div>
      </section>

      {/* Prediction Prompt Section */}
      <section className="prediction-section">
        <div className="container">
          <div className="prediction-prompt">
            <div className="prediction-content">
              <div className="prediction-icon">
                <Sparkles className="prediction-main-icon" />
              </div>
              <h3 className="prediction-title">
                What Do You See Coming Next?
              </h3>
              <p className="prediction-subtitle">
                Every great prediction starts with curiosity and ends with discovery. Whether you're sensing the next breakthrough in technology, foreseeing cultural shifts, or anticipating changes that will shape our world, your voice adds to our collective wisdom about tomorrow.
              </p>
              <div className="prediction-stats">
                <div className="mini-stat">
                  <span className="mini-stat-number">{stats.predictionsToday}</span>
                  <span className="mini-stat-label">predictions today</span>
                </div>
                <div className="mini-stat">
                  <span className="mini-stat-number">{stats.predictorsToday}</span>
                  <span className="mini-stat-label">Active Predictors</span>
                </div>
              </div>
            </div>
            <div className="prediction-action">
              <button className="prediction-cta" onClick={handlePredictionSubmit}>
                <div className="prediction-cta-content">
                  <div className="prediction-cta-icon">
                    <Lightbulb />
                  </div>
                  <div className="prediction-cta-text">
                    <span className="prediction-cta-title">Submit Your Prediction</span>
                    <span className="prediction-cta-subtitle">
                      {isLoggedIn ? 'Add your voice to the conversation' : 'Join thousands of forward-thinkers'}
                    </span>
                  </div>
                  <div className="prediction-cta-arrow">
                    <ArrowRight />
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="features-two-column">
            <div className="features-highlight">
              <div className="section-badge">WHY PREDICT</div>
              <h2 className="section-title-bold">
                Be Part of Something<br />
                <span className="highlight-gold">Extraordinary</span><br />
                Where <span className="highlight-gold">Your Ideas</span><br />
                Shape Tomorrow
              </h2>
            </div>
            <div className="features-desc">
              <p className="section-subtitle-large">
                Trendphile is more than a platform—it's a platform where curious minds gather to explore possibilities, share insights, and engage in meaningful conversations about what lies ahead. Your predictions become part of a larger tapestry of human foresight and imagination.
              </p>
            </div>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <Star />
              </div>
              <h3 className="feature-title">Express Your Vision</h3>
              <p className="feature-description">
                Share your unique perspective on what's coming next. From breakthrough technologies to cultural movements, your insights help paint a richer picture of tomorrow's possibilities.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Users />
              </div>
              <h3 className="feature-title">Connect & Discover</h3>
              <p className="feature-description">
                Explore predictions from fellow visionaries around the world. Find fascinating perspectives, engage with ideas that challenge your thinking, and discover trends you never considered.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Target />
              </div>
              <h3 className="feature-title">Track Your Accuracy</h3>
              <p className="feature-description">
                See how your predictions unfold over time. Build your reputation as a forward-thinker and celebrate those moments when your vision becomes reality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Community Guidelines Section */}
      <section className="guidelines-section">
        <div className="container">
          <div className="guidelines-two-column">
            <div className="guidelines-highlight">
              <div className="section-badge">COMMUNITY</div>
              <h2 className="section-title-bold">
                Prediction <span className="highlight-gold">Guidelines</span><br />
                for Quality<br />
                <span className="highlight-gold">Contributions</span>
              </h2>
            </div>
            <div className="guidelines-desc">
              <p className="section-subtitle-large">
                To maintain the quality and integrity of our prediction platform, we've established clear guidelines for submissions. These standards help ensure that every prediction adds meaningful value to our collective exploration of the future and contributes to building a comprehensive database of thoughtful forecasts.
              </p>
            </div>
          </div>

          <div className="guidelines-grid">
            <div className="guideline-card">
              <div className="guideline-icon">
                <Shield />
              </div>
              <h3 className="guideline-title">No Spam or Duplicates</h3>
              <p className="guideline-description">
                Avoid submitting the same prediction multiple times or flooding the platform with similar content. Each prediction should be unique and add new value to our collective foresight database.
              </p>
              <div className="guideline-examples">
                <div className="example-item good">
                  <CheckCircle size={16} />
                  <span>Unique predictions with new insights</span>
                </div>
                <div className="example-item avoid">
                  <AlertTriangle size={16} />
                  <span>Avoid repeating identical predictions</span>
                </div>
              </div>
            </div>

            <div className="guideline-card">
              <div className="guideline-icon">
                <BookOpen />
              </div>
              <h3 className="guideline-title">Meaningful Content</h3>
              <p className="guideline-description">
                Submit well-thought-out predictions with clear timeframes and context. Quality predictions help build a valuable resource for understanding future trends and possibilities across all categories.
              </p>
              <div className="guideline-examples">
                <div className="example-item good">
                  <CheckCircle size={16} />
                  <span>Specific predictions with timeframes</span>
                </div>
                <div className="example-item avoid">
                  <AlertTriangle size={16} />
                  <span>Avoid vague or meaningless statements</span>
                </div>
              </div>
            </div>

            <div className="guideline-card">
              <div className="guideline-icon">
                <Target />
              </div>
              <h3 className="guideline-title">Appropriate Categories</h3>
              <p className="guideline-description">
                Choose the most relevant category for your prediction to help organize our database effectively. Proper categorization makes it easier to track trends and analyze patterns across different fields.
              </p>
              <div className="guideline-examples">
                <div className="example-item good">
                  <CheckCircle size={16} />
                  <span>Match predictions to correct categories</span>
                </div>
                <div className="example-item good">
                  <CheckCircle size={16} />
                  <span>Use "Other" when none fit perfectly</span>
                </div>
              </div>
            </div>

            <div className="guideline-card">
              <div className="guideline-icon">
                <Heart />
              </div>
              <h3 className="guideline-title">Respectful Predictions</h3>
              <p className="guideline-description">
                Keep your predictions respectful and appropriate. Avoid offensive language, harmful content, or predictions that could promote negativity or harm towards individuals or groups.
              </p>
              <div className="guideline-examples">
                <div className="example-item good">
                  <CheckCircle size={16} />
                  <span>Constructive and positive forecasts</span>
                </div>
                <div className="example-item avoid">
                  <AlertTriangle size={16} />
                  <span>No offensive or harmful content</span>
                </div>
              </div>
            </div>
          </div>

          <div className="guidelines-footer">
            <div className="guidelines-cta">
              <Award className="guidelines-cta-icon" />
              <div className="guidelines-cta-content">
                <h3 className="guidelines-cta-title">Ready to Share Your Prediction?</h3>
                <p className="guidelines-cta-subtitle">
                  Follow our guidelines to ensure your predictions contribute meaningfully to our growing database of future insights.
                </p>
              </div>
              <button className="guidelines-cta-button" onClick={handlePrimaryAction}>
                <ArrowRight size={20} />
                {isLoggedIn ? 'Submit Prediction' : 'Join & Predict'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{formatNumber(stats.totalUsers)}</div>
              <div className="stat-label">members</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.dailyPredictions}</div>
              <div className="stat-label">Daily Predictions</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.weeklyPredictions || Math.floor(stats.dailyPredictions * 0.23)}</div>
              <div className="stat-label">Predictions This Week</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.activePredictors}+</div>
              <div className="stat-label">Active This Month</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="about-section">
        <div className="container">
          <div className="about-two-column">
            <div className="about-highlight">
              <div className="section-badge">OUR STORY</div>
              <h2 className="section-title-bold">
                About <span className="highlight-gold">Trendphile</span><br />
                <span className="highlight-gold">Where Curiosity Meets Community</span>
              </h2>
            </div>
            <div className="about-desc">
              <p className="section-subtitle-large">
                At Trendphile, we believe that everyone has unique insights about the future waiting to be shared. We've created a space where curious minds can explore, predict, and discuss what's coming next across every aspect of human experience. Whether you're passionate about technology, culture, society, or any field that sparks your imagination, this is your platform to contribute to our collective understanding of tomorrow.
                <br /><br />
                Join our growing community of forward-thinkers, visionaries, and anyone who loves pondering the possibilities that lie ahead.
              </p>
            </div>
          </div>

          <div className="about-grid">
            <div className="about-card">
              <div className="about-icon">
                <Globe />
              </div>
              <h3 className="about-title">Our Mission</h3>
              <p className="about-description">
                To create a welcoming space where people from all walks of life can share their predictions, explore fascinating possibilities, and engage in thoughtful conversations about the future we're all building together.
              </p>
            </div>

            <div className="about-card">
              <div className="about-icon">
                <Mail />
              </div>
              <h3 className="about-title">Get In Touch</h3>
              <p className="about-description">
                Have questions, suggestions, or just want to share your thoughts about the future? We'd love to hear from you! Our platform thrives on connection and conversation.
              </p>
              <div className="contact-methods">
                <a href="mailto:info@trendphile.com" className="contact-link">
                  <Mail className="contact-icon" />
                  info@trendphile.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              <span className="hero-title-main">Ready to Share Your</span><br />
              <span className="hero-title-accent">Vision of Tomorrow?</span>
            </h2>
            <p className="cta-subtitle">
              Join thousands of curious minds who are already exploring what's next. Your predictions/expectations, insights, and perspectives help create a richer understanding of the possibilities ahead. The future is waiting for your voice.
            </p>
            <div className="hero-cta-group">
              <button className="cta-primary" onClick={handlePrimaryAction}>
                <Lightbulb />
                {isLoggedIn ? 'Make Your Prediction' : 'Start Predicting'}
              </button>
              <button className="cta-secondary" onClick={handleSecondaryAction}>
                <Eye />
                View Analytics
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="social-media-section">
        <div className="container">
          <div className="social-content">
            <h2 className="social-title">Follow Us on Social Media</h2>
            <p className="social-subtitle">
              Stay updated with the latest trends, insights, and community highlights
            </p>
            
            <div className="social-links">
              <a 
                href="https://t.me/trendphile" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link telegram"
                aria-label="Follow us on Telegram"
              >
                <svg viewBox="0 0 24 24" className="social-icon">
                  <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 0 0-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                </svg>
                <span>Telegram</span>
              </a>

              <a 
                href="https://www.linkedin.com/company/trendphile-official" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link linkedin"
                aria-label="Follow us on LinkedIn"
              >
                <svg viewBox="0 0 24 24" className="social-icon">
                  <path fill="currentColor" d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                </svg>
                <span>LinkedIn</span>
              </a>

              <a 
  href="https://x.com/TrendphileAI" 
  target="_blank" 
  rel="noopener noreferrer"
  className="social-link twitter"
  aria-label="Follow us on X/Twitter"
>

                <svg viewBox="0 0 24 24" className="social-icon">
                  <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span>X</span>
                              </a>

              <a 
                href="#" 
                className="social-link instagram disabled"
                aria-label="Instagram (Coming Soon)"
                onClick={(e) => e.preventDefault()}
              >
                <svg viewBox="0 0 24 24" className="social-icon">
                  <path fill="currentColor" d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/>
                </svg>
                <span>Instagram</span>
                <span className="coming-soon-badge">Soon</span>
              </a>

              <a 
                href="https://www.youtube.com/channel/UC4_Pgfmh6ImNk0WgJOoR4lA" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link youtube"
                aria-label="Follow us on YouTube"
              >
                <svg viewBox="0 0 24 24" className="social-icon">
                  <path fill="currentColor" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                <span>YouTube</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
